<template>
  <!-- 结果流：第一张官方示例卡片 + 用户全部生成历史。
       点击卡片看详情；hover 右下 Use 按钮把该结果/示例的设置应用到左侧表单。 -->
  <div class="results-feed">
    <div class="feed-head">
      <!-- Clapperboard 场记板图标（muted 色） -->
      <svg class="feed-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M20.2 6 3 11l-.9-2.4c-.3-1.1.3-2.2 1.3-2.5l13.5-4c1.1-.3 2.2.3 2.5 1.3Z"/>
        <path d="m6.2 5.3 3.1 3.9"/>
        <path d="m12.4 3.4 3.1 4"/>
        <path d="M3 11h18v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z"/>
      </svg>
      <h2>Your generations</h2>
    </div>

    <!-- 空状态：示例被删且没有任何生成记录 -->
    <div v-if="isEmpty" class="feed-empty">
      <div class="feed-empty-icon">✨</div>
      <h3>Nothing here yet</h3>
      <p>Describe your video on the left and hit Generate — or click the Example button above the prompt to start from a full demo.</p>
    </div>

    <div class="feed-grid">
      <!-- 官方示例卡片（新示例在前；用户删除后 localStorage 按 id 持久隐藏；Example 按钮不受影响） -->
      <div v-for="tpl in shownExamples" :key="tpl.id" class="feed-card">
        <button type="button" class="card-media" title="View details" @click="exampleDetail = tpl">
          <video
            v-if="tpl.previewVideo"
            :src="tpl.previewVideo"
            :poster="tpl.thumbnail || undefined"
            muted
            loop
            playsinline
            preload="metadata"
            @mouseenter="playVideo($event)"
            @mouseleave="resetVideo($event)"
          />
          <div v-else class="media-fallback">✨</div>
        </button>
        <span class="card-badge">Example</span>
        <button type="button" class="use-btn" title="Load this example's prompt, references and settings into the form" @click.stop="$emit('use-example', tpl.id)">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m2 9 3-3 3 3"/><path d="M13 18H7a2 2 0 01-2-2V6"/><path d="m22 15-3 3-3-3"/><path d="M11 6h6a2 2 0 012 2v10"/></svg>
          Remix
        </button>
        <button type="button" class="del-btn" title="Delete" aria-label="Delete" @click.stop="pendingDelete = { type: 'example', id: tpl.id }">✕</button>
      </div>

      <!-- 用户生成历史 -->
      <div v-for="v in videos" :key="v.id" class="feed-card">
        <button type="button" class="card-media" title="View details" @click="detailVideo = v">
          <video
            v-if="v.taskStatus === 'COMPLETED' && v.resultUrl"
            :src="v.resultUrl"
            muted
            loop
            playsinline
            preload="metadata"
            @mouseenter="playVideo($event)"
            @mouseleave="resetVideo($event)"
          />
          <div v-else-if="v.taskStatus === 'FAILED'" class="media-state failed">
            <span class="state-icon">✕</span>
            <span>Failed</span>
          </div>
          <div v-else class="media-state">
            <span class="feed-spin" />
            <span>Processing</span>
          </div>
        </button>
        <span v-if="v.taskCreatedAt" class="card-caption">{{ new Date(v.taskCreatedAt).toLocaleDateString() }}</span>
        <button type="button" class="use-btn" title="Load this generation's prompt, references and settings into the form" @click.stop="$emit('use-video', v)">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m2 9 3-3 3 3"/><path d="M13 18H7a2 2 0 01-2-2V6"/><path d="m22 15-3 3-3-3"/><path d="M11 6h6a2 2 0 012 2v10"/></svg>
          Remix
        </button>
        <button
          type="button"
          class="del-btn"
          :disabled="isRecentProcessing(v)"
          :title="isRecentProcessing(v) ? PROCESSING_DELETE_MSG : 'Delete'"
          aria-label="Delete"
          @click.stop="!isRecentProcessing(v) && (pendingDelete = { type: 'video', video: v })"
        >✕</button>
      </div>
    </div>
    <div v-if="loading && !videos.length" class="feed-loading">Loading…</div>

    <!-- 删除确认弹窗（示例卡片 / 历史结果通用） -->
    <Teleport to="body">
      <div v-if="pendingDelete" class="confirm-overlay" @click.self="pendingDelete = null">
        <div class="confirm-dialog">
          <h3>{{ pendingDelete.type === 'example' ? 'Remove the example?' : 'Delete this video?' }}</h3>
          <p>
            {{ pendingDelete.type === 'example'
              ? 'The example card will be removed from this list. You can still apply it anytime with the Example button above the prompt.'
              : "This video will be deleted from your generations. This can't be undone." }}
          </p>
          <div class="confirm-actions">
            <button type="button" class="confirm-btn ghost" :disabled="deleting" @click="pendingDelete = null">Cancel</button>
            <button type="button" class="confirm-btn danger" :disabled="deleting" @click="confirmDelete">
              {{ deleting ? 'Deleting…' : pendingDelete.type === 'example' ? 'Remove' : 'Delete' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- 历史详情（共用组件） -->
    <VideoDetailDialog v-if="detailVideo" :video="detailVideo" @close="detailVideo = null" />

    <!-- 示例详情 -->
    <Teleport to="body">
      <div v-if="exampleDetail" class="ex-overlay" @click="exampleDetail = null">
        <div class="ex-dialog" @click.stop>
          <div class="ex-head">
            <h2>{{ exampleDetail.name || 'Example' }}</h2>
            <div class="ex-head-right">
              <button type="button" class="ex-use-btn" @click="onExampleUse">
                Use this example
              </button>
              <button type="button" class="ex-close" aria-label="Close" @click="exampleDetail = null">✕</button>
            </div>
          </div>
          <div class="ex-body">
            <video
              v-if="exampleDetail.previewVideo"
              :src="exampleDetail.previewVideo"
              controls
              muted
              playsinline
              preload="metadata"
              class="ex-video"
            />
            <p v-if="exampleDetail.description" class="ex-desc">{{ exampleDetail.description }}</p>
            <div class="ex-section-row">
              <h3 class="ex-section">Prompt</h3>
              <CopyPromptButton :text="exampleDetail.prompt ?? ''" />
            </div>
            <!-- @image1 token 渲染成「缩略图 + token + 底色」chip（展示层，存储不动） -->
            <MentionPromptText
              class="ex-prompt"
              :prompt="exampleDetail.prompt ?? ''"
              :image-urls="exampleRefImages"
              :video-urls="exampleRefVideos"
            />
            <template v-if="(exampleDetail.referenceMedia?.length ?? 0) > 0 || (exampleDetail.referenceAudio?.length ?? 0) > 0">
              <h3 class="ex-section">References</h3>
              <!-- 统一方形块 + @token 角标，点击盖层放大预览 -->
              <ReferenceAssetsGrid
                :image-urls="exampleRefImages"
                :video-urls="exampleRefVideos"
                :audio-urls="exampleRefAudios"
              />
            </template>
            <h3 class="ex-section">Settings</h3>
            <p class="ex-settings">
              {{ exampleDetail.aspectRatio }} · {{ exampleDetail.duration }}s · {{ exampleDetail.resolution }} · {{ exampleDetail.bitrate || exampleDetail.bitrateMode || 'standard' }}
            </p>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import api from '~/api';
import CopyPromptButton from '~/components/seedance/CopyPromptButton.vue';
import MentionPromptText from '~/components/seedance/MentionPromptText.vue';
import ReferenceAssetsGrid from '~/components/seedance/ReferenceAssetsGrid.vue';
import VideoDetailDialog from '~/components/seedance/VideoDetailDialog.vue';
import { useSnackbarStore } from '~/composables/useSnackBarStore';

const props = defineProps<{ examples?: any[] }>();
const emit = defineEmits<{
  (e: 'use-example', id?: string): void;
  (e: 'use-video', video: any): void;
}>();

const snackbar = useSnackbarStore();

const videos = ref<any[]>([]);
const loading = ref(true);
const detailVideo = ref<any | null>(null);
// 示例详情弹窗当前查看的模板（null = 关闭）
const exampleDetail = ref<any | null>(null);

// 示例卡片被用户删除后的持久标记（只隐藏卡片；prompt 区的 Example 按钮不受影响）
const EXAMPLE_DISMISSED_KEY = 'seedance-example-dismissed';
// 默认显示（多数用户未删过）；已删用户 mounted 后立即隐藏（按模板 id 记忆）
const dismissedIds = ref<Set<string>>(new Set());
onMounted(() => {
  const ids = new Set<string>();
  for (const tpl of props.examples ?? []) {
    if (localStorage.getItem(`${EXAMPLE_DISMISSED_KEY}:${tpl.id}`) === '1') ids.add(tpl.id);
  }
  // 旧版单示例时代的删除标记只对应 sample-image-6
  if (localStorage.getItem(EXAMPLE_DISMISSED_KEY) === '1') ids.add('sample-image-6');
  if (ids.size) dismissedIds.value = ids;
});
const shownExamples = computed(() =>
  (props.examples ?? []).filter((t: any) => !dismissedIds.value.has(t.id)),
);

// 示例素材按类型拆分（text 组承担 image 角色，与创作页一致：format !== 'video' 归 image）
const exampleRefImages = computed<string[]>(() =>
  (exampleDetail.value?.referenceMedia ?? [])
    .filter((r: any) => r.format !== 'video')
    .map((r: any) => r.url),
);
const exampleRefVideos = computed<string[]>(() =>
  (exampleDetail.value?.referenceMedia ?? [])
    .filter((r: any) => r.format === 'video')
    .map((r: any) => r.url),
);
const exampleRefAudios = computed<string[]>(() =>
  (exampleDetail.value?.referenceAudio ?? []).map((a: any) => a.url),
);
const isEmpty = computed(() => !shownExamples.value.length && !loading.value && !videos.value.length);

// 待确认的删除对象：示例卡片 或 某条历史结果
const pendingDelete = ref<{ type: 'example'; id: string } | { type: 'video'; video: any } | null>(null);
const deleting = ref(false);

// 生成中且未满 24h：不给删（任务已扣费，删掉会把生成中的结果变孤儿、失败也无法退款）。
// 超过 24h 仍未结束视为卡住，放开以便清理。
const PROCESSING_DELETE_MSG = 'This task is still processing. You can delete it once it finishes.';
const isRecentProcessing = (v: any) =>
  v.taskStatus !== 'COMPLETED' &&
  v.taskStatus !== 'FAILED' &&
  !!v.taskCreatedAt &&
  Date.now() - new Date(v.taskCreatedAt).getTime() < 86_400_000;

const confirmDelete = async () => {
  const pending = pendingDelete.value;
  if (!pending) return;
  if (pending.type === 'example') {
    localStorage.setItem(`${EXAMPLE_DISMISSED_KEY}:${pending.id}`, '1');
    const next = new Set(dismissedIds.value);
    next.add(pending.id);
    dismissedIds.value = next;
    pendingDelete.value = null;
    return;
  }
  deleting.value = true;
  try {
    await api.seedanceDeleteVideo(pending.video.orderId);
    snackbar.showSuccess('Deleted');
    pendingDelete.value = null;
    await fetchVideos();
  } catch (err: any) {
    console.error(err);
    snackbar.showError(
      err?.response?.status === 409 ? PROCESSING_DELETE_MSG : err?.message || 'Delete failed',
    );
  } finally {
    deleting.value = false;
  }
};

// 有生成中的任务时每 8s 轮询翻新（把"生成中"卡片翻新为完成/失败），
// 全部结束就停止（cron 兜底长任务）；组件卸载时清理定时器。
let pollTimer: ReturnType<typeof setTimeout> | null = null;
const clearPoll = () => {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
};
const hasProcessing = () =>
  videos.value.some(
    (v: any) => v.taskStatus !== 'COMPLETED' && v.taskStatus !== 'FAILED',
  );

const fetchVideos = async () => {
  loading.value = true;
  try {
    const res = await api.seedanceListVideos({ limit: 24 });
    videos.value = res.videos;
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
  }
  // 有处理中任务就 8s 后再拉一次；全部结束则停止轮询
  clearPoll();
  if (hasProcessing()) pollTimer = setTimeout(fetchVideos, 8000);
};
onMounted(fetchVideos);
onBeforeUnmount(clearPoll);
defineExpose({ refresh: fetchVideos });

const onExampleUse = () => {
  emit('use-example', exampleDetail.value?.id);
  exampleDetail.value = null;
};

// hover 自动播放的静音预览
const playVideo = (e: MouseEvent) => {
  const v = (e.currentTarget as HTMLElement).querySelector('video');
  v?.play().catch(() => {});
};
const resetVideo = (e: MouseEvent) => {
  const v = (e.currentTarget as HTMLElement).querySelector('video');
  if (!v) return;
  v.pause();
  v.currentTime = 0;
};
</script>

<style scoped>
.results-feed { width: 100%; color: white; }
.feed-head {
  display: flex; align-items: center; gap: 0.5rem;
  margin-bottom: 1rem;
}
.feed-head h2 { margin: 0; font-size: 0.875rem; font-weight: 600; color: rgba(255, 255, 255, 0.85); }
.feed-icon { flex-shrink: 0; color: rgba(255, 255, 255, 0.5); }
.feed-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}
@media (min-width: 1024px) {
  .feed-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
.feed-card {
  position: relative;
  overflow: hidden;
  border-radius: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.4);
}
.card-media {
  display: block;
  width: 100%;
  aspect-ratio: 1;
  padding: 0; border: none;
  background: rgba(255, 255, 255, 0.04);
  cursor: pointer;
  overflow: hidden;
}
.card-media video { width: 100%; height: 100%; object-fit: cover; }
.media-fallback {
  display: grid; place-items: center;
  width: 100%; height: 100%;
  font-size: 1.5rem; opacity: 0.5;
}
.media-state {
  display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 0.35rem;
  width: 100%; height: 100%;
  font-size: 0.75rem; color: rgba(255, 255, 255, 0.5);
}
.media-state.failed { color: #f87171; }
.state-icon { font-size: 1.25rem; }
.feed-spin {
  width: 20px; height: 20px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-top-color: #d946ef;
  animation: feed-spin 1s linear infinite;
}
@keyframes feed-spin { to { transform: rotate(360deg); } }
/* 低调深灰胶囊（不再用品牌色底） */
.card-badge {
  pointer-events: none;
  position: absolute; top: 0.5rem; left: 0.5rem;
  padding: 2px 6px;
  border-radius: 0.35rem;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(8px);
  color: rgba(255, 255, 255, 0.9);
  font-size: 10px; font-weight: 600;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
}
.card-caption {
  pointer-events: none;
  position: absolute; bottom: 0.5rem; left: 0.5rem;
  font-size: 10px; color: white;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.9);
}
/* hover 右下角：应用该结果的设置到表单（触屏常显） */
.use-btn {
  position: absolute; bottom: 0.5rem; right: 0.5rem; z-index: 10;
  display: inline-flex; align-items: center; gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border: none; border-radius: 0.35rem;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(8px);
  color: rgba(255, 255, 255, 0.9);
  font-size: 10px; font-weight: 500;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
  transition: opacity 0.15s, background 0.15s;
}
.use-btn:hover { background: rgba(255, 255, 255, 0.22); color: white; }
@media (hover: hover) {
  .use-btn { opacity: 0; }
  .feed-card:hover .use-btn { opacity: 1; }
}
/* 卡片右上角删除按钮（触发确认弹窗；触屏常显） */
.del-btn {
  position: absolute; top: 0.35rem; right: 0.35rem; z-index: 10;
  width: 22px; height: 22px;
  border: none; border-radius: 50%;
  background: rgba(0, 0, 0, 0.7);
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px; line-height: 1;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
  transition: opacity 0.15s, background 0.15s, color 0.15s;
}
.del-btn:hover { background: #000; color: #fff; }
@media (hover: hover) {
  .del-btn { opacity: 0; }
  .feed-card:hover .del-btn { opacity: 1; }
}
/* 生成中未满 24h：置灰不可点（即便 hover 也保持低透明度） */
.del-btn:disabled { opacity: 0.4 !important; cursor: not-allowed; }
.del-btn:disabled:hover { background: rgba(0, 0, 0, 0.7); color: rgba(255, 255, 255, 0.8); }
/* 空状态 */
.feed-empty {
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  padding: 5rem 1rem;
  text-align: center;
}
.feed-empty-icon {
  width: 4rem; height: 4rem;
  border-radius: 50%;
  background: rgba(217, 70, 239, 0.1);
  display: flex; align-items: center; justify-content: center;
  font-size: 1.75rem;
  margin-bottom: 1rem;
}
.feed-empty h3 { margin: 0 0 0.5rem; font-size: 1.05rem; font-weight: 600; }
.feed-empty p { margin: 0; max-width: 20rem; font-size: 0.8rem; color: rgba(255, 255, 255, 0.5); }
/* 删除确认弹窗 */
.confirm-overlay {
  position: fixed; inset: 0; z-index: 120;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  padding: 1rem;
}
.confirm-dialog {
  width: 100%; max-width: 24rem;
  background: #131313;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  padding: 1.25rem;
  color: white;
}
.confirm-dialog h3 { margin: 0 0 0.5rem; font-size: 1rem; font-weight: 700; }
.confirm-dialog p { margin: 0 0 1rem; font-size: 0.8rem; color: rgba(255, 255, 255, 0.55); line-height: 1.5; }
.confirm-actions { display: flex; justify-content: flex-end; gap: 0.5rem; }
.confirm-btn {
  padding: 0.45rem 1rem;
  border: none; border-radius: 0.5rem;
  font-size: 0.8rem; font-weight: 500;
  cursor: pointer;
}
.confirm-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.confirm-btn.ghost {
  background: transparent;
  color: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(255, 255, 255, 0.15);
}
.confirm-btn.ghost:hover:not(:disabled) { background: rgba(255, 255, 255, 0.06); color: white; }
.confirm-btn.danger { background: #dc2626; color: white; }
.confirm-btn.danger:hover:not(:disabled) { background: #b91c1c; }
.feed-loading {
  padding: 2rem 1rem;
  text-align: center;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.4);
}

/* 示例详情弹窗 */
.ex-overlay {
  position: fixed; inset: 0; z-index: 100;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  padding: 1rem;
}
.ex-dialog {
  display: flex; flex-direction: column;
  width: 100%; max-width: 42rem; max-height: 85vh;
  background: #131313;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  color: white;
}
.ex-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}
.ex-head h2 { margin: 0; font-size: 1rem; font-weight: 700; }
.ex-head-right { display: flex; align-items: center; gap: 0.5rem; }
.ex-use-btn {
  padding: 0.35rem 0.75rem;
  border: none; border-radius: 0.5rem;
  background: linear-gradient(90deg, #d946ef, #f43f5e);
  color: white;
  font-size: 0.75rem; font-weight: 500;
  cursor: pointer;
}
.ex-use-btn:hover { opacity: 0.9; }
.ex-close {
  background: transparent; border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: 1rem; cursor: pointer;
}
.ex-close:hover { color: white; }
.ex-body { overflow-y: auto; padding: 1rem 1.25rem 1.25rem; }
.ex-video {
  display: block;
  margin: 0 auto 1rem;
  max-height: 40vh; max-width: 100%;
  border-radius: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: black;
}
.ex-desc { margin: 0 0 0.75rem; font-size: 0.75rem; color: rgba(255, 255, 255, 0.55); }
.ex-section {
  margin: 1rem 0 0.25rem;
  font-size: 11px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.45);
}
/* Prompt 标题 + 一键复制按钮同行 */
.ex-section-row {
  display: flex; align-items: center; justify-content: space-between;
  margin: 1rem 0 0.25rem;
}
.ex-section-row .ex-section { margin: 0; }
.ex-prompt {
  max-height: 10rem; overflow-y: auto;
  white-space: pre-wrap;
  margin: 0;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.75rem;
}
.ex-settings { margin: 0; font-size: 0.75rem; color: rgba(255, 255, 255, 0.55); }
</style>
